import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { VideoTab, EditorMode } from '../types';
import { VideoModel } from '../components/VideoModelChoice';
import { apiPostUploadVideo } from 'services/apiService';
import { apiPostGenerate, apiPostCancelGeneration } from '../../../services/api/generationApi';
import { useJobStatus } from '../../../hooks/useJobStatus';
import { useVideosFromFirestore } from 'hooks/useMediaFromFirestore';
import { downloadFromSignedUrl } from '../../../utils/saveFileWithDialog';
import { useThread } from './ThreadContext';
import { useSettings } from './SettingsContext';
import { useFeedback } from './FeedbackContext';

interface VideoContextValue {
  // State
  editorMode: EditorMode;
  selectedVideo: IVideo | null;
  activeVideoTab: VideoTab;
  setActiveVideoTab: (tab: VideoTab) => void;
  videoEditPrompt: string;
  setVideoEditPrompt: (prompt: string) => void;
  videoModel: VideoModel;
  setVideoModel: (model: VideoModel) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  musicFile: { file: File; url: string; name: string } | null;
  setMusicFile: (file: { file: File; url: string; name: string } | null) => void;
  videoPrompt: string;
  setVideoPrompt: (prompt: string) => void;
  isVideoLoading: boolean;
  isSubmitting: boolean;
  shotsPrompts: string[];
  setShotsPrompts: (prompts: string[]) => void;
  endFrame: SecondaryImage | null;
  videoHistory: IVideo[];
  reversedVideoHistory: IVideo[];
  videosLoading: boolean;
  activeVideoJobId: string | null;

  // Actions
  handleGenerateVideo: () => void;
  handleEditVideo: () => void;
  handleExtendVideo: () => void;
  handleSelectVideoForEditing: (video: IVideo) => void;
  handleExitVideoMode: () => void;
  handleDownloadVideo: () => void;
  handleUploadVideo: (file: File | undefined) => Promise<void>;
  handleEndFrameUpload: (files: File[]) => void;
  handleEndFrameRemove: (img: SecondaryImage) => void;
  handleCancelVideoGeneration: () => Promise<void>;

  // Shared setters needed by EditorContext for cross-context resets
  setEditorMode: (mode: EditorMode) => void;
  setSelectedVideo: (video: IVideo | null) => void;
  setIsVideoLoading: (loading: boolean) => void;
  setEndFrame: (frame: SecondaryImage | null) => void;

  // Shared utility
  getFinalPrompt: (prompt: string) => string;

  // Mutable refs for cross-context communication (set by EditorContext / LandingPageInner)
  _currentImageRef: React.MutableRefObject<IImage | null>;
  _setErrorRef: React.MutableRefObject<(error: string | null) => void>;
  _setLoadingMessageRef: React.MutableRefObject<(msg: string) => void>;
}

const VideoContext = createContext<VideoContextValue | null>(null);

export const useVideo = (): VideoContextValue => {
  const ctx = useContext(VideoContext);
  if (!ctx) throw new Error('useVideo must be used within a VideoProvider');
  return ctx;
};

interface VideoProviderProps {
  children: React.ReactNode;
  db: any;
  uid: string;
}

const noop = () => {};

export const VideoProvider: React.FC<VideoProviderProps> = ({ children, db, uid }) => {
  const { currentProjectId, currentThreadId } = useThread();
  const { useBrandGuidelines, excludedAssetIds } = useSettings();
  const { _editorModeRef, _videoElRef } = useFeedback();

  // Mutable refs for cross-context communication
  const _currentImageRef = useRef<IImage | null>(null);
  const _setErrorRef = useRef<(error: string | null) => void>(noop);
  const _setLoadingMessageRef = useRef<(msg: string) => void>(noop);

  // State
  const [editorMode, setEditorMode] = useState<EditorMode>('picture');
  const [selectedVideo, setSelectedVideo] = useState<IVideo | null>(null);
  const [activeVideoTab, setActiveVideoTab] = useState<VideoTab>('Editing');
  const [videoEditPrompt, setVideoEditPrompt] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [musicFile, setMusicFile] = useState<{ file: File; url: string; name: string } | null>(null);
  const [videoModel, setVideoModel] = useState<VideoModel>('google');
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [videoPrompt, setVideoPrompt] = useState<string>('');
  const [shotsPrompts, setShotsPrompts] = useState<string[]>([]);
  const [endFrame, setEndFrame] = useState<SecondaryImage | null>(null);
  const [activeVideoJobId, setActiveVideoJobId] = useState<string | null>(null);

  const { status: videoJobStatus, errorMessage: videoJobError, clearJob: clearVideoJob } = useJobStatus(db, currentProjectId, activeVideoJobId);

  // Firestore video data
  const { videos: firestoreVideos, loading: videosLoading, error: videosError } = useVideosFromFirestore(
    db,
    currentProjectId,
    currentThreadId,
    false
  );

  const videoHistory = firestoreVideos;
  const reversedVideoHistory = [...videoHistory].reverse();
  const prevVideoHistoryLength = useRef(videoHistory.length);

  // Handle video Firestore errors
  useEffect(() => {
    if (videosError) {
      _setErrorRef.current(`Failed to load videos: ${videosError}`);
    }
  }, [videosError]);

  // Keep FeedbackContext refs in sync
  useEffect(() => {
    _editorModeRef.current = editorMode;
  }, [editorMode, _editorModeRef]);

  useEffect(() => {
    _videoElRef.current = videoRef.current;
  });

  // Auto-select newly generated video when in video edit mode
  useEffect(() => {
    if (editorMode === 'video' && videoHistory.length > prevVideoHistoryLength.current) {
      const latestVideo = videoHistory[videoHistory.length - 1];
      if (latestVideo.created_by === uid) {
        setSelectedVideo(latestVideo);
      }
    }
    prevVideoHistoryLength.current = videoHistory.length;
  }, [videoHistory.length, editorMode, uid, videoHistory]);

  // Cleanup end frame blob URL
  useEffect(() => {
    return () => {
      if (endFrame && endFrame.url.startsWith('blob:')) {
        URL.revokeObjectURL(endFrame.url);
      }
    };
  }, [endFrame]);

  // Handle job status transitions
  useEffect(() => {
    if (!videoJobStatus || !activeVideoJobId) return;

    if (videoJobStatus === 'completed' || videoJobStatus === 'cancelled') {
      setActiveVideoJobId(null);
      clearVideoJob();
    } else if (videoJobStatus === 'failed') {
      _setErrorRef.current(videoJobError || 'Video generation failed');
      setActiveVideoJobId(null);
      clearVideoJob();
    }
  }, [videoJobStatus, videoJobError, activeVideoJobId, clearVideoJob]);

  const getFinalPrompt = useCallback((tempPrompt: string): string => {
    let finalPrompt = tempPrompt;
    if (shotsPrompts.length > 0) {
      finalPrompt += (finalPrompt.trim().endsWith('.') ? ' ' : ', ') + shotsPrompts.join(', ');
    }
    return finalPrompt;
  }, [shotsPrompts]);

  const handleCancelVideoGeneration = useCallback(async () => {
    if (!activeVideoJobId || !currentProjectId) return;
    try {
      await apiPostCancelGeneration(activeVideoJobId, currentProjectId);
    } catch (err) {
      console.error('Failed to cancel video generation:', err);
    }
    setActiveVideoJobId(null);
    clearVideoJob();
  }, [activeVideoJobId, currentProjectId, clearVideoJob]);

  const handleGenerateVideo = useCallback(async () => {
    const img = _currentImageRef.current;
    if (!img) { _setErrorRef.current('No image loaded to edit.'); return; }
    if (!videoPrompt.trim()) { _setErrorRef.current('Please enter a description for your edit.'); return; }
    if (!currentProjectId || !currentThreadId) { _setErrorRef.current('Project or Thread ID is missing.'); return; }

    _setErrorRef.current(null);
    setIsSubmitting(true);

    try {
      const endFrameBlob = endFrame
        ? await fetch(endFrame.url).then((r) => r.blob())
        : undefined;

      const { job_id } = await apiPostGenerate({
        operation: 'generate_video',
        project_id: currentProjectId,
        thread_id: currentThreadId,
        picture_id: img.picture_id,
        prompt: getFinalPrompt(videoPrompt),
        model: videoModel,
        end_frame: endFrameBlob,
        music_file: videoModel === 'google' ? musicFile?.file : undefined,
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
      });
      setActiveVideoJobId(job_id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      _setErrorRef.current(`Failed to start video generation. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [endFrame, videoPrompt, videoModel, currentProjectId, currentThreadId, getFinalPrompt, useBrandGuidelines, excludedAssetIds, musicFile]);

  const handleEditVideo = useCallback(async () => {
    if (!selectedVideo) { _setErrorRef.current('No video selected to edit.'); return; }
    if (!videoEditPrompt.trim()) { _setErrorRef.current('Please enter a description for your video edit.'); return; }
    if (!currentProjectId || !currentThreadId) { _setErrorRef.current('Project or Thread ID is missing.'); return; }

    _setErrorRef.current(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: 'edit_video',
        project_id: currentProjectId,
        thread_id: currentThreadId,
        video_id: selectedVideo.video_id,
        prompt: videoEditPrompt,
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
      });
      setActiveVideoJobId(job_id);
      setVideoEditPrompt('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      _setErrorRef.current(`Failed to start video edit. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedVideo, videoEditPrompt, currentProjectId, currentThreadId, useBrandGuidelines, excludedAssetIds]);

  const handleExtendVideo = useCallback(async () => {
    if (!selectedVideo) { _setErrorRef.current('No video selected to extend.'); return; }
    if (!currentProjectId || !currentThreadId) { _setErrorRef.current('Project or Thread ID is missing.'); return; }

    _setErrorRef.current(null);
    setIsSubmitting(true);

    try {
      const { job_id } = await apiPostGenerate({
        operation: 'extend_video',
        project_id: currentProjectId,
        thread_id: currentThreadId,
        video_id: selectedVideo.video_id,
        prompt: videoEditPrompt,
        use_brand_guidelines: useBrandGuidelines,
        excluded_asset_ids: excludedAssetIds,
      });
      setActiveVideoJobId(job_id);
      setVideoEditPrompt('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      _setErrorRef.current(`Failed to start video extension. ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedVideo, videoEditPrompt, currentProjectId, currentThreadId, useBrandGuidelines, excludedAssetIds]);

  const handleSelectVideoForEditing = useCallback((video: IVideo) => {
    setSelectedVideo(video);
    setEditorMode('video');
    setActiveVideoTab('Editing');
    setVideoEditPrompt('');
  }, []);

  const handleExitVideoMode = useCallback(() => {
    setEditorMode('picture');
    setSelectedVideo(null);
    setVideoEditPrompt('');
    setActiveVideoTab('Editing');
  }, []);

  const handleDownloadVideo = useCallback(async () => {
    if (!selectedVideo) return;
    await downloadFromSignedUrl(selectedVideo.output_url, `video-${Date.now()}.mp4`);
  }, [selectedVideo]);

  const handleEndFrameUpload = useCallback(async (files: File[]) => {
    const file = files[0];
    setEndFrame({
      id: `${Date.now()}-endFrame`,
      file: file,
      url: URL.createObjectURL(file),
      alt: file.name
    });
  }, []);

  const handleEndFrameRemove = useCallback(async (img: SecondaryImage) => {
    if (img.url.startsWith('blob:')) URL.revokeObjectURL(img.url);
    setEndFrame(null);
  }, []);

  const handleUploadVideo = useCallback(async (file: File | undefined) => {
    if (file && currentProjectId && currentThreadId) {
      setIsVideoLoading(true);
      _setLoadingMessageRef.current('Uploading video...');
      try {
        await apiPostUploadVideo(file, currentProjectId, currentThreadId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        _setErrorRef.current(`Failed to upload video. ${errorMessage}`);
      } finally {
        setIsVideoLoading(false);
        _setLoadingMessageRef.current('');
      }
    }
  }, [currentProjectId, currentThreadId]);

  return (
    <VideoContext.Provider value={{
      editorMode,
      selectedVideo,
      activeVideoTab,
      setActiveVideoTab,
      videoEditPrompt,
      setVideoEditPrompt,
      videoModel,
      setVideoModel,
      videoRef,
      musicFile,
      setMusicFile,
      videoPrompt,
      setVideoPrompt,
      isVideoLoading,
      isSubmitting,
      shotsPrompts,
      setShotsPrompts,
      endFrame,
      videoHistory,
      reversedVideoHistory,
      videosLoading,
      activeVideoJobId,
      handleGenerateVideo,
      handleEditVideo,
      handleExtendVideo,
      handleSelectVideoForEditing,
      handleExitVideoMode,
      handleDownloadVideo,
      handleUploadVideo,
      handleEndFrameUpload,
      handleEndFrameRemove,
      handleCancelVideoGeneration,
      setEditorMode,
      setSelectedVideo,
      setIsVideoLoading,
      setEndFrame,
      getFinalPrompt,
      _currentImageRef,
      _setErrorRef,
      _setLoadingMessageRef,
    }}>
      {children}
    </VideoContext.Provider>
  );
};
